import { MenuCategory, MenuItems } from '@open-tender/types'

export type FilterItems = (items: MenuItems) => MenuItems

// `nested` means the category has subcategories in the menu, whether or not
// any of them currently have visible products.
export interface NestedGroup {
  category: MenuCategory
  children: MenuCategory[]
  nested: boolean
}

// `startId` is set on the first section of a nested group. The group becomes
// active when its sticky header reaches the sticky line, which is earlier than
// its first section because that section sits below the header's margin.
export interface NestedSection {
  id: string
  groupId: string
  startId?: string
}

// `top` is relative to the document, `offset` is the sticky line in pixels.
export interface SectionPosition {
  id: string
  top: number
  offset: number
}

export const makeGroupId = (categoryId: number) =>
  `nested-category-${categoryId}`

export const makeSubcategoryId = (categoryId: number, childId: number) =>
  `nested-subcategory-${categoryId}-${childId}`

export const makeOtherSectionId = (slug: string) => `nested-other-${slug}`

export const hasSubcategories = (categories: MenuCategory[]) =>
  categories.some((category) => category.children.length > 0)

export const makeNestedGroup = (
  category: MenuCategory,
  filterItems: FilterItems
): NestedGroup => ({
  category,
  nested: category.children.length > 0,
  children: category.children.filter(
    (child) => filterItems(child.items).length > 0
  ),
})

// Drops categories that would render nothing: a nested category with no
// visible subcategory, or a flat category with no visible products.
export const makeNestedGroups = (
  categories: MenuCategory[],
  filterItems: FilterItems
): NestedGroup[] =>
  categories
    .map((category) => makeNestedGroup(category, filterItems))
    .filter((group) =>
      group.nested
        ? group.children.length > 0
        : filterItems(group.category.items).length > 0
    )

export const makeGroupsSignature = (groups: NestedGroup[]) =>
  groups
    .map(
      ({ category, children }) =>
        `${category.id}:${children.map((child) => child.id).join(',')}`
    )
    .join('|')

export const getGroupTargetId = ({ category, children }: NestedGroup) =>
  children.length > 0
    ? makeSubcategoryId(category.id, children[0].id)
    : makeGroupId(category.id)

// Sections in document order.
export const makeSections = (
  groups: NestedGroup[],
  otherIds: string[] = []
): NestedSection[] => [
  ...otherIds.map((id) => ({ id, groupId: id })),
  ...groups.reduce<NestedSection[]>((sections, group) => {
    const groupId = makeGroupId(group.category.id)
    if (!group.nested) return [...sections, { id: groupId, groupId }]
    return [
      ...sections,
      ...group.children.map((child, index) => ({
        id: makeSubcategoryId(group.category.id, child.id),
        groupId,
        ...(index === 0 && { startId: groupId }),
      })),
    ]
  }, []),
]

// Uses the menu container's end rather than the document's so the footer does
// not have to be scrolled through. A menu that fits on screen has not been
// scrolled, so it keeps its first section active.
export const hasReachedMenuEnd = ({
  scrollY,
  containerBottom,
  viewportHeight,
  tolerance = 2,
}: {
  scrollY: number
  containerBottom: number
  viewportHeight: number
  tolerance?: number
}) => scrollY > 0 && containerBottom <= viewportHeight + tolerance

export const getActiveSectionId = (
  positions: SectionPosition[],
  scrollY: number,
  reachedEnd: boolean
): string | null => {
  if (positions.length === 0) return null
  if (reachedEnd) return positions[positions.length - 1].id
  return positions.reduce(
    (activeId, position) =>
      position.top - position.offset <= scrollY + 1 ? position.id : activeId,
    positions[0].id
  )
}

export const scrollRowTo = (row: HTMLElement, left: number) => {
  if (typeof row.scrollTo === 'function') {
    row.scrollTo({ left, behavior: 'smooth' })
  } else {
    row.scrollLeft = left
  }
}
